import { LoadingButton } from "@mui/lab";
import { Box, Button, FormHelperText, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import {
  CheckoutState,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItemName } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { useCheckout } from "components/StripeCheckout/useCheckout";
import { getParams, getSource } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import CardWrapper from "../components/CardWrapper";
import ManageEmailVolumeDialog from "./ManageEmailVolumeDialog";
import styles from "./styles";

const AddOnsEmailVolume = () => {
  const source = getSource();
  const navigate = useNavigate();
  const {
    subscription,
    addOns,
    setTab,
    setOpenCheckoutDialog,
    upgradeDialogSource,
  } = useContext(SubscriptionContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { isAtMaxEmailVolume, canPurchaseMoreEmailVolume } = useCheckout();
  const { checkoutLoading } = useSubscription();
  const [openManageDialog, setOpenManageDialog] = useState(false);

  const itemName = currentOrg?.isVaUser
    ? AddOnItemName.email_volume_monthly
    : AddOnItemName.email_volume_pro_monthly;
  const isAutomated = itemName === AddOnItemName.email_volume_monthly;

  const canPurchase = canPurchaseMoreEmailVolume(itemName, 1);
  const hasPurchased =
    subscription?.emailVolumes && subscription?.emailVolumes.length > 0;

  const handleManage = () => {
    setOpenManageDialog(true);
  };

  const handleCheckout = async () => {
    setTab(SubscriptionTab.monthly);
    const state: CheckoutState = {
      plan: itemName,
      source,
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    if (upgradeDialogSource) {
      setOpenCheckoutDialog(state);
    } else {
      navigate(`/${routes.subscriptions}`, {
        state,
      });
    }
  };

  const BENEFITS = [
    {
      icon: "📮",
      primary: `Get ${isAutomated ? "150" : "50"} additional ${isAutomated ? "automated " : ""}emails each month`,
      secondary: <>Expand your reach and connect with more brands</>,
    },
  ];

  const quantity =
    subscription?.emailVolumes?.reduce((sum, v) => sum + v.quantity, 0) || 0;

  if (isAtMaxEmailVolume()) {
    return <></>;
  }

  return (
    <CardWrapper>
      <Grid item>
        <Typography variant="h5" component="div" gutterBottom>
          🔥 Increase your {isAutomated ? "automated " : ""}email volume
        </Typography>

        <Typography variant="h6" sx={{ color: "green.main" }} gutterBottom>
          ${addOns?.[itemName]?.price.toFixed(2)}
          /mo
        </Typography>

        <Typography gutterBottom>
          Level up your outreach — send more brand pitches each month starting
          at just ${addOns?.[itemName]?.price.toFixed(2)}.
        </Typography>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography>
                <strong>{benefit.primary}</strong>
              </Typography>
              <Typography>{benefit.secondary}</Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Grid container item alignItems="center" gap={1}>
        {canPurchase && (
          <LoadingButton
            variant="contained"
            onClick={handleCheckout}
            disableElevation
            loading={checkoutLoading === itemName}
          >
            Purchase
          </LoadingButton>
        )}

        {hasPurchased && (
          <Button
            disableElevation
            onClick={handleManage}
            sx={{
              px: 2,
              color: "primary.main",
              backgroundColor: "#f4f8f6",
            }}
          >
            Manage{" "}
          </Button>
        )}
        <Grid item xs={12}>
          <FormHelperText sx={{ color: grey[600], mt: 1 }}>
            {hasPurchased && quantity > 0 ? (
              <em>
                You have purchased{" "}
                <strong>
                  {quantity} {quantity === 1 ? "package" : "packages"}
                </strong>
                . Click on Manage to view details.
              </em>
            ) : (
              <em>
                Limit {addOns[itemName]?.max_quantity} per account.{" "}
                <strong>Save up to 33.3%</strong> by paying annually.
              </em>
            )}
          </FormHelperText>
        </Grid>
      </Grid>

      <ManageEmailVolumeDialog
        open={openManageDialog}
        onClose={() => setOpenManageDialog(false)}
      />
    </CardWrapper>
  );
};

export default AddOnsEmailVolume;
